// In your new file, e.g., smartie-frontend/src/utils/logging.ts

// Extend the Window interface to include smartieAiData if it's not already defined globally
// This helps with TypeScript type checking.
interface SmartieAiData {
  loggingEnabledOverride?: boolean | string | number; // Allow string/number due to wp_localize_script behavior
  // Add other properties of smartieAiData here as needed
  statusApiUrl?: string;
  restNonce?: string;
  // etc.
}

declare global {
  interface Window {
    smartieAiData?: SmartieAiData;
  }
}

function shouldDisplayLogs(): boolean {
  const override = window.smartieAiData?.loggingEnabledOverride;

  if (typeof override === "boolean") {
    return override;
  }
  if (typeof override === "string") {
    if (override === "1") return true;
    if (override === "0") return false;
  }
  if (typeof override === "number") {
    if (override === 1) return true;
    if (override === 0) return false;
  }

  // Fallback logic if the WordPress setting isn't available or not a recognized value
  // Vite uses import.meta.env.DEV for development mode detection.
  return import.meta.env.DEV;
}

// Deduplication cache to prevent console spam for identical messages (T-3.4)

const loggedMessages = new Set<string>();

function shouldLogUnique(key: string): boolean {
  if (loggedMessages.has(key)) {
    return false;
  }
  loggedMessages.add(key);
  return true;
}

function formatArgs(args: unknown[]): string {
  try {
    return args
      .map((a) => (typeof a === "string" ? a : JSON.stringify(a)))
      .join(" | ");
  } catch {
    return args.join(" ");
  }
}

// Overwrite existing log functions to include deduplication (logs once per unique arg signature)
export function logDebug(...args: unknown[]): void {
  if (!shouldDisplayLogs()) return;
  const key = "debug:" + formatArgs(args);
  if (shouldLogUnique(key)) {
    console.debug(...args);
  }
}

export function logInfo(...args: unknown[]): void {
  if (!shouldDisplayLogs()) return;
  const key = "info:" + formatArgs(args);
  if (shouldLogUnique(key)) {
    console.info(...args);
  }
}

export function logWarn(...args: unknown[]): void {
  if (!shouldDisplayLogs()) return;
  const key = "warn:" + formatArgs(args);
  if (shouldLogUnique(key)) {
    console.warn(...args);
  }
}

export function logError(...args: unknown[]): void {
  // Error logs always show in development; in production respect setting
  if (!shouldDisplayLogs() && import.meta.env.PROD) return;
  const key = "error:" + formatArgs(args);
  if (shouldLogUnique(key)) {
    console.error(...args);
  }
}
